import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getDisputeDetail,
  updateDisputeStatus,
  resolveDispute,
  addAdminEvidence,
  escalateDispute,
  closeDispute,
  IMAGE_BASE_URL
} from '../../services/api';
import { extractItemData } from '../../utils/apiHelpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import Button from '../../components/common/Button';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';

const DisputeDetail = ({ admin }) => {
  const { disputeId } = useParams();
  const navigate = useNavigate();
  const [dispute, setDispute] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [resolveData, setResolveData] = useState({
    resolution: '',
    resolution_notes: '',
    refund_amount: '',
    admin_notes: ''
  });
  const [evidenceData, setEvidenceData] = useState({
    description: '',
    file_url: '',
    file_name: '',
    evidence_type: 'document'
  });
  const [escalateData, setEscalateData] = useState({
    escalation_reason: ''
  });

  useEffect(() => {
    fetchDispute();
  }, [disputeId]);

  const fetchDispute = async () => {
    try {
      setLoading(true);
      const response = await getDisputeDetail(disputeId);
      const { item, attachments, dispute: disputeData, order } = extractItemData(response);
      setDispute(item || disputeData);
      setEvidence(attachments || []);
      // Note: messages might be in different response structure
      if (response.data && response.data.messages) {
        setMessages(response.data.messages);
      }
    } catch (err) {
      Alert.error(err.message || 'Failed to fetch dispute');
      navigate('/admin/disputes');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateDisputeStatus(disputeId, { status: newStatus });
      Alert.success(`Status updated to ${newStatus}`);
      fetchDispute();
    } catch (err) {
      Alert.error('Failed to update status');
    }
  };

  const handleResolve = async () => {
    if (!resolveData.resolution || !resolveData.resolution_notes) {
      Alert.error('Resolution and notes are required');
      return;
    }

    try {
      await resolveDispute(disputeId, resolveData);
      Alert.success('Dispute resolved successfully');
      setShowResolveModal(false);
      setResolveData({
        resolution: '',
        resolution_notes: '',
        refund_amount: '',
        admin_notes: ''
      });
      fetchDispute();
    } catch (err) {
      Alert.error('Failed to resolve dispute');
    }
  };

  const handleAddEvidence = async () => {
    if (!evidenceData.description) {
      Alert.error('Description is required');
      return;
    }

    try {
      await addAdminEvidence(disputeId, evidenceData);
      Alert.success('Evidence added');
      setShowEvidenceModal(false);
      setEvidenceData({
        description: '',
        file_url: '',
        file_name: '',
        evidence_type: 'document'
      });
      fetchDispute();
    } catch (err) {
      Alert.error('Failed to add evidence');
    }
  };

  const handleEscalate = async () => {
    if (!escalateData.escalation_reason) {
      Alert.error('Escalation reason is required');
      return;
    }

    try {
      await escalateDispute(disputeId, escalateData);
      Alert.success('Dispute escalated');
      setShowEscalateModal(false);
      setEscalateData({ escalation_reason: '' });
      fetchDispute();
    } catch (err) {
      Alert.error('Failed to escalate dispute');
    }
  };

  const handleCloseDispute = async () => {
    if (window.confirm('Are you sure you want to close this dispute? This action cannot be undone.')) {
      try {
        await closeDispute(disputeId);
        Alert.success('Dispute closed');
        fetchDispute();
      } catch (err) {
        Alert.error('Failed to close dispute');
      }
    }
  };

  const getCategoryLabel = (category) => {
    const categories = {
      'item_not_as_described': 'Item Not as Described',
      'item_not_received': 'Item Not Received',
      'damaged_item': 'Damaged Item',
      'wrong_item': 'Wrong Item',
      'seller_unresponsive': 'Seller Unresponsive',
      'delivery_issue': 'Delivery Issue',
      'quality_issue': 'Quality Issue',
      'other': 'Other'
    };
    return categories[category] || category;
  };

  const getResolutionLabel = (resolution) => {
    const resolutions = {
      'refund_full': 'Full Refund to Buyer',
      'refund_partial': 'Partial Refund',
      'release_funds': 'Release Funds to Seller',
      'split_funds': 'Split Funds',
      'cancel_order': 'Cancel Order'
    };
    return resolutions[resolution] || resolution;
  };

  if (loading || !dispute) {
    return <LoadingSpinner />;
  }

  const isResolved = dispute.status === 'resolved' || dispute.status === 'closed';
  const canResolve = dispute.status === 'pending' || dispute.status === 'in_review';
  const canEscalate = !isResolved && admin.role === 'moderator';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate('/admin/disputes')}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
          >
            ← Back to Disputes
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Dispute #{dispute.dispute_id}</h1>
          <div className="flex items-center space-x-4 mt-2">
            <span className="text-gray-600">Order #{dispute.order_id}</span>
            <StatusBadge status={dispute.status} type="dispute" />
            {dispute.resolution && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                {getResolutionLabel(dispute.resolution)}
              </span>
            )}
            <span className="text-sm text-gray-500">
              Created: {dispute.formatted_date || new Date(dispute.created_at).toLocaleString()}
            </span>
          </div>
        </div>
        
        <div className="flex space-x-2">
          {canResolve && (
            <Button
              variant="primary"
              onClick={() => setShowResolveModal(true)}
            >
              Resolve Dispute
            </Button>
          )}
          {canEscalate && (
            <Button
              variant="warning"
              onClick={() => setShowEscalateModal(true)}
            >
              Escalate
            </Button>
          )}
          {dispute.status === 'resolved' && (
            <Button
              variant="success"
              onClick={handleCloseDispute}
            >
              Close Dispute
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Dispute Info */}
        <div className="space-y-6">
          {/* Parties */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-lg mb-4">Parties Involved</h3>
            <div className="space-y-4">
              <div className="p-3 border border-blue-200 rounded-lg bg-blue-50">
                <div className="flex items-center space-x-3 mb-2">
                  {dispute.raised_by_profile_picture ? (
                    <img 
                      src={dispute.raised_by_profile_picture} 
                      alt="Buyer" 
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="font-medium text-blue-800">
                        {dispute.raised_by_first_name?.[0]}{dispute.raised_by_last_name?.[0]}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium">Buyer (Complainant)</p>
                    <p className="text-sm text-gray-600">
                      {dispute.raised_by_first_name} {dispute.raised_by_last_name}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-700">{dispute.raised_by_email}</p>
              </div>
              
              <div className="p-3 border border-red-200 rounded-lg bg-red-50">
                <div className="flex items-center space-x-3 mb-2">
                  {dispute.raised_against_profile_picture ? (
                    <img 
                      src={dispute.raised_against_profile_picture} 
                      alt="Seller" 
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="font-medium text-red-800">
                        {dispute.raised_against_first_name?.[0]}{dispute.raised_against_last_name?.[0]}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium">Seller (Respondent)</p>
                    <p className="text-sm text-gray-600">
                      {dispute.raised_against_first_name} {dispute.raised_against_last_name}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-700">{dispute.raised_against_email}</p>
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-lg mb-4">Order Details</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-500">Order ID</label>
                <p className="font-medium">#{dispute.order_id}</p>
              </div>
              
              <div>
                <label className="text-sm text-gray-500">Total Amount</label>
                <p className="font-medium">${dispute.total_amount}</p>
              </div>
              
              <div>
                <label className="text-sm text-gray-500">Order Status</label>
                <StatusBadge status={dispute.order_status} type="order" />
              </div>
              
              <div>
                <label className="text-sm text-gray-500">Escrow Status</label>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  dispute.escrow_status === 'held' ? 'bg-yellow-100 text-yellow-800' :
                  dispute.escrow_status === 'released' ? 'bg-green-100 text-green-800' :
                  dispute.escrow_status === 'refunded' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {dispute.escrow_status || 'Not in escrow'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-lg mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Update Status
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={dispute.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="in_review">In Review</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              
              <Button
                fullWidth
                variant="outline"
                onClick={() => setShowEvidenceModal(true)}
              >
                Add Admin Evidence
              </Button>
              
              {dispute.support_ticket_id && (
                <Button
                  fullWidth
                  variant="outline"
                  onClick={() => navigate(`/admin/support/tickets/${dispute.support_ticket_id}`)}
                >
                  View Support Ticket
                </Button>
              )}
              
              <Button
                fullWidth
                variant="outline"
                onClick={() => navigate(`/admin/orders/${dispute.order_id}`)}
              >
                View Order Details
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column - Dispute Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Dispute Information */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-lg mb-4">Dispute Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-sm text-gray-500">Category</label>
                <p className="font-medium">{getCategoryLabel(dispute.category)}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Days Open</label>
                <p className="font-medium">{dispute.days_open || 0} days</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Evidence Count</label>
                <p className="font-medium">{evidence.length} items</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Last Updated</label>
                <p className="font-medium">
                  {dispute.formatted_updated_date || new Date(dispute.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="text-sm text-gray-500">Description</label>
              <p className="mt-1 text-gray-700 whitespace-pre-line">{dispute.description}</p>
            </div>
            
            {dispute.admin_notes && (
              <div className="border-t pt-4">
                <label className="text-sm text-gray-500">Admin Notes</label>
                <p className="mt-1 text-gray-700 whitespace-pre-line">{dispute.admin_notes}</p>
              </div>
            )}
            
            {dispute.resolution_notes && (
              <div className="border-t pt-4">
                <label className="text-sm text-gray-500">Resolution Notes</label>
                <p className="mt-1 text-gray-700 whitespace-pre-line">{dispute.resolution_notes}</p>
              </div>
            )}
          </div>

          {/* Listing Details */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-lg mb-4">Listing Details</h3>
            <div className="flex items-start space-x-4">
              {dispute.listing_image && (
                <img
                src={`${IMAGE_BASE_URL}${dispute.listing_image}`}
                alt={dispute.listing_title}
                className="w-24 h-24 object-cover rounded-lg"
              />           
              )}
              <div className="flex-1">
                <h4 className="font-medium text-lg">{dispute.listing_title}</h4>
                <p className="text-gray-600 text-sm mt-1">{dispute.listing_description}</p>
                <p className="font-medium text-gray-900 mt-2">${dispute.listing_price}</p>
              </div>
            </div>
          </div>

          {/* Evidence */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg">Evidence</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowEvidenceModal(true)}
              >
                + Add Evidence
              </Button>
            </div>
            
            {evidence.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No evidence submitted yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {evidence.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">Evidence #{index + 1}</p>
                        <p className="text-sm text-gray-600">
                          {item.formatted_date || new Date(item.created_at).toLocaleString()}
                        </p>
                      </div>
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100">
                        {item.uploaded_by === 0 ? 'Admin' : 'User'}
                      </span>
                    </div>
                    
                    <p className="mt-2 text-gray-700">{item.description}</p>
                    
                    {item.file_url && (
                      <div className="mt-3">
                        <a
                          href={item.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                        >
                          <span>📎</span>
                          <span className="text-sm">{item.file_name || 'View File'}</span>
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resolve Modal */}
      <Modal
        isOpen={showResolveModal}
        onClose={() => {
          setShowResolveModal(false);
          setResolveData({
            resolution: '',
            resolution_notes: '',
            refund_amount: '',
            admin_notes: ''
          });
        }}
        title="Resolve Dispute"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resolution Type *
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={resolveData.resolution}
              onChange={(e) => setResolveData({ ...resolveData, resolution: e.target.value })}
            >
              <option value="">Select Resolution</option>
              <option value="refund_full">Full Refund to Buyer</option>
              <option value="refund_partial">Partial Refund</option>
              <option value="release_funds">Release Funds to Seller</option>
              <option value="split_funds">Split Funds</option>
              <option value="cancel_order">Cancel Order</option>
            </select>
          </div>
          
          {(resolveData.resolution === 'refund_partial' || resolveData.resolution === 'split_funds') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Refund Amount (${dispute.total_amount} available)
              </label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={resolveData.refund_amount}
                onChange={(e) => setResolveData({ ...resolveData, refund_amount: e.target.value })}
                placeholder="Enter amount"
                min="0"
                max={dispute.total_amount}
                step="0.01"
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resolution Notes *
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md h-32"
              value={resolveData.resolution_notes}
              onChange={(e) => setResolveData({ ...resolveData, resolution_notes: e.target.value })}
              placeholder="Explain the resolution decision..."
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Admin Notes
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md h-24"
              value={resolveData.admin_notes}
              onChange={(e) => setResolveData({ ...resolveData, admin_notes: e.target.value })}
              placeholder="Internal notes (visible only to admins)..."
            />
          </div>
          
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowResolveModal(false);
                setResolveData({
                  resolution: '',
                  resolution_notes: '',
                  refund_amount: '',
                  admin_notes: ''
                });
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleResolve}
              disabled={!resolveData.resolution || !resolveData.resolution_notes}
            >
              Resolve Dispute
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Evidence Modal */}
      <Modal
        isOpen={showEvidenceModal}
        onClose={() => {
          setShowEvidenceModal(false);
          setEvidenceData({
            description: '',
            file_url: '',
            file_name: '',
            evidence_type: 'document'
          });
        }}
        title="Add Admin Evidence"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md h-32"
              value={evidenceData.description}
              onChange={(e) => setEvidenceData({ ...evidenceData, description: e.target.value })}
              placeholder="Describe the evidence..."
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              File URL (optional)
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={evidenceData.file_url}
              onChange={(e) => setEvidenceData({ ...evidenceData, file_url: e.target.value })}
              placeholder="https://example.com/file.pdf"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              File Name (optional)
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={evidenceData.file_name}
              onChange={(e) => setEvidenceData({ ...evidenceData, file_name: e.target.value })}
              placeholder="evidence-document.pdf"
            />
          </div>
          
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowEvidenceModal(false);
                setEvidenceData({
                  description: '',
                  file_url: '',
                  file_name: '',
                  evidence_type: 'document'
                });
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddEvidence}
              disabled={!evidenceData.description}
            >
              Add Evidence
            </Button>
          </div>
        </div>
      </Modal>

      {/* Escalate Modal */}
      <Modal
        isOpen={showEscalateModal}
        onClose={() => {
          setShowEscalateModal(false);
          setEscalateData({ escalation_reason: '' });
        }}
        title="Escalate Dispute"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              Escalating this dispute will notify senior administrators for review. 
              Please provide a detailed reason for escalation.
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Escalation Reason *
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md h-32"
              value={escalateData.escalation_reason}
              onChange={(e) => setEscalateData({ ...escalateData, escalation_reason: e.target.value })}
              placeholder="Why does this dispute require senior admin attention?..."
              required
            />
          </div>
          
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowEscalateModal(false);
                setEscalateData({ escalation_reason: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={handleEscalate}
              disabled={!escalateData.escalation_reason}
            >
              Escalate Dispute
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DisputeDetail;